import { useEffect } from 'react';

import { Box, Switch, SxProps, Theme, Typography } from '@mui/material';
import { Control, Controller, UseFormSetValue, FieldValues } from 'react-hook-form';

import { ProfileFieldConfig } from '../types/profile.types';

type ProfileToggleFieldProps = Readonly<{
  field: ProfileFieldConfig;
  control: Control;
  setValue: UseFormSetValue<FieldValues>;
}>;

export const ProfileToggleField = ({ field, control, setValue }: ProfileToggleFieldProps) => {
  const backendValueAsBoolean = field.defaultValue === '1';

  // Update form value when backend value changes (handles external updates)
  useEffect(() => {
    setValue(field.key, backendValueAsBoolean);
  }, [backendValueAsBoolean, field.key, setValue]);

  if (field?.type.variant !== 'TOGGLE') {
    return null;
  }

  return (
    <Controller
      key={`${field.key}-${backendValueAsBoolean}`}
      name={field.key}
      control={control}
      defaultValue={backendValueAsBoolean}
      render={({ field: controllerField }) => {
        // For backend changes: setValue updates the form field
        // For user clicks: use form value for immediate optimistic updates
        const isChecked = controllerField.value ?? backendValueAsBoolean;

        if (field.type.variant !== 'TOGGLE') {
          return <></>;
        }

        return (
          <Box
            sx={containerSx}
            onClick={() => {
              const newValue = !isChecked;

              // Update form state immediately for optimistic UI
              controllerField.onChange(newValue);

              // For toggles with setsValue, pass the toggle state to let ProfilePage handle the logic
              // Otherwise, convert boolean to number for API (1 for true, 0 for false)
              if (field.setsValue) {
                field.onChange?.(newValue ? [1] : [0]);
              } else {
                field.onChange?.([newValue ? 1 : 0]);
              }
            }}
          >
            <Switch checked={isChecked} sx={switchSx} disableRipple />

            <Typography
              variant="text-sm-medium"
              color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
            >
              {field.type.subText}
            </Typography>
          </Box>
        );
      }}
    />
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: '30px',
  height: '100%',
  gap: backrs.spacing['spacing-md'],
  cursor: 'pointer',
});

const switchSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',

  width: backrs.spacing['spacing-4xl'],
  height: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-none'],
  '& .MuiSwitch-switchBase': {
    padding: backrs.spacing['spacing-none'],
    height: backrs.spacing['spacing-xl'],
    '&.Mui-checked': {
      transform: `translateX(${backrs.spacing['spacing-xl']})`,

      color: backrs.colors['Colors/Base/White'],
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: backrs.colors['Colors/Background/bg-brand-solid'],
        borderColor: backrs.colors['Colors/Background/bg-brand-solid'],
      },
      '& .MuiSwitch-thumb': {
        borderColor: backrs.colors['Colors/Background/bg-brand-solid'],
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: backrs.spacing['spacing-xl'],
    height: backrs.spacing['spacing-xl'],
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    boxShadow: 'none',
    backgroundColor: backrs.colors['Colors/Base/White'],

    transition: 'width 300ms ease-in-out',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    boxSizing: 'border-box',
    '&.Mui-checked': {
      backgroundColor: backrs.colors['Colors/Base/White'],
    },
  },
});
